import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { publicAPI } from '../services/api';
import {
  LiveKitRoom,
  useParticipants,
  RoomAudioRenderer,
} from '@livekit/components-react';
import '@livekit/components-styles';
import { useWakeLock, useAudioKeepAlive } from '../hooks/useWakeLock';
import { MuteButton, AudioOutputToggle, VolumeControl } from '../components/AudioControls';
import PlayStorePrompt from '../components/PlayStorePrompt';
import './MeetingRoom.css';

// ── Live duration counter ─────────────────────────────────────────────────────
function useLiveDuration(startedAt) {
  const [mins, setMins] = useState(0);
  useEffect(() => {
    if (!startedAt) return;
    const tick = () => setMins(Math.floor((Date.now() - new Date(startedAt)) / 60000));
    tick();
    const id = setInterval(tick, 30000);
    return () => clearInterval(id);
  }, [startedAt]);
  return mins;
}

// ── Meeting meta shown in header ──────────────────────────────────────────────
function MeetingMeta({ meeting }) {
  const liveMins = useLiveDuration(meeting?.started_at);
  if (!meeting) return null;

  const isInstant = meeting.meeting_kind === 'instant';
  const fmt = (iso) =>
    new Date(iso).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' });

  return (
    <div className="guest-meeting-meta">
      <span className={`guest-meta-badge${isInstant ? ' guest-meta-badge--instant' : ' guest-meta-badge--planned'}`}>
        {isInstant ? '⚡ Instant Meet' : '📅 Scheduled Meet'}
      </span>
      {meeting.started_at && (
        <span className="guest-meta-info">
          Started {fmt(meeting.started_at)} · <span className="guest-meta-live">{liveMins}m ongoing</span>
        </span>
      )}
      {!meeting.started_at && !isInstant && meeting.scheduled_for && (
        <span className="guest-meta-info">Scheduled {fmt(meeting.scheduled_for)}</span>
      )}
    </div>
  );
}

// ── Participant list ──────────────────────────────────────────────────────────
function ParticipantList({ canSeeParticipants, inviteToken, guestId }) {
  const liveParticipants = useParticipants();
  const [canSee, setCanSee] = useState(canSeeParticipants);

  useEffect(() => {
    const check = async () => {
      try {
        const res = await publicAPI.getParticipants(inviteToken, guestId);
        setCanSee(res.data.can_see_participants);
      } catch (e) { console.error('visibility check failed', e); }
    };
    check();
    const id = setInterval(check, 5000);
    return () => clearInterval(id);
  }, [inviteToken, guestId]);

  if (!canSee) {
    return (
      <div className="participants-list">
        <h3>👥 Participants</h3>
        <p className="visibility-notice">🔒 Participant list is hidden by the host</p>
        <p className="participant-count">{liveParticipants.length} participant(s) in meeting</p>
      </div>
    );
  }

  return (
    <div className="participants-list">
      <h3>👥 Participants ({liveParticipants.length})</h3>
      {liveParticipants.map((p) => (
        <div key={p.sid} className="participant-item">
          <span>{p.name || p.identity}</span>
          <span className={p.isSpeaking ? 'speaking' : ''}>
            {p.isSpeaking ? '🎤 Speaking' : '🔇 Muted'}
          </span>
        </div>
      ))}
    </div>
  );
}

// ── Room content ──────────────────────────────────────────────────────────────
function RoomContent({ canSeeParticipants, inviteToken, guestId }) {
  const { requestWakeLock, releaseWakeLock } = useWakeLock();
  const { startKeepAlive, stopKeepAlive } = useAudioKeepAlive();

  useEffect(() => {
    requestWakeLock();
    startKeepAlive();
    return () => { releaseWakeLock(); stopKeepAlive(); };
  }, [requestWakeLock, releaseWakeLock, startKeepAlive, stopKeepAlive]);

  return (
    <div className="room-container">
      <div className="audio-info">
        <h2>🎙️ Audio Meeting</h2>
        <p className="status-connected">🟢 Connected as Guest</p>
        <p className="info-text">🎤 Your microphone is active. Speak to participate.</p>
      </div>
      <div className="audio-controls-bar">
        <MuteButton />
        <AudioOutputToggle />
        <VolumeControl />
      </div>
      <ParticipantList
        canSeeParticipants={canSeeParticipants}
        inviteToken={inviteToken}
        guestId={guestId}
      />
      <RoomAudioRenderer />
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
function GuestMeetingRoom() {
  const { token: inviteToken } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [error, setError]                     = useState('');
  const [canSeeParticipants, setCanSeeParticipants] = useState(false);
  const [showFeedback, setShowFeedback]        = useState(false);
  // Disconnect from LiveKit immediately on leave — stops audio before popup
  const [disconnected, setDisconnected]        = useState(false);
  const postNavRef = useRef(null);
  // Guards onDisconnected from firing the "meeting ended" path when the guest
  // is leaving voluntarily (setDisconnected triggers onDisconnected too).
  const intentionalLeaveRef = useRef(false);

  const stateData     = location.state || {};
  const meeting       = stateData.meeting;
  const livekitToken  = stateData.token;
  const livekitUrl    = stateData.livekitUrl;
  const guestName     = stateData.guestName;
  const guestId       = stateData.guestId;
  const initialCanSee = stateData.canSeeParticipants;

  useEffect(() => {
    if (!meeting || !livekitToken || !livekitUrl) {
      setError('Invalid meeting data. Please use the invite link again.');
    } else {
      setCanSeeParticipants(initialCanSee || false);
    }
  }, [meeting, livekitToken, livekitUrl, initialCanSee]);

  const triggerFeedback = useCallback((navFn) => {
    postNavRef.current = navFn;
    setShowFeedback(true);
  }, []);

  const handleFeedbackDone = useCallback(() => {
    setShowFeedback(false);
    if (postNavRef.current) postNavRef.current();
  }, []);

  const handleDisconnect = useCallback(() => {
    // Voluntary leave handles its own navigation — don't double-fire here.
    if (intentionalLeaveRef.current) return;
    sessionStorage.removeItem(`guest_name_${inviteToken}`);
    triggerFeedback(() =>
      navigate('/meeting-ended', {
        state: { meetingTitle: meeting?.title, guestName, reason: 'ended' },
      })
    );
  }, [navigate, meeting, guestName, inviteToken, triggerFeedback]);

  const handleLeave = async () => {
    intentionalLeaveRef.current = true;
    setDisconnected(true); // stop my audio immediately, before popup
    try {
      if (guestId && inviteToken) await publicAPI.leaveAsGuest(inviteToken, guestId);
    } catch (e) { console.error('leave failed:', e); }
    triggerFeedback(() =>
      navigate('/guest-left', {
        state: { inviteToken, guestName, meetingTitle: meeting?.title },
      })
    );
  };

  if (error) {
    return (
      <div className="meeting-room error-screen">
        <h2>❌ {error}</h2>
        <button onClick={() => navigate('/')} className="btn-primary">Go Home</button>
      </div>
    );
  }

  if (!meeting || !livekitToken) {
    return (
      <div className="meeting-room loading-screen">
        <div className="spinner" />
        <h2>Loading meeting...</h2>
      </div>
    );
  }

  return (
    <div className="meeting-room">
      <header className="meeting-header">
        <div>
          <h1>🎤 {meeting.title}</h1>
          <MeetingMeta meeting={meeting} />
          <p style={{ margin: '0.25rem 0 0', fontSize: '0.82rem', color: 'rgba(255,255,255,0.45)' }}>
            Joined as <strong style={{ color: 'rgba(255,255,255,0.7)' }}>{guestName}</strong> · Guest
          </p>
        </div>
        <div className="meeting-controls">
          <button onClick={handleLeave} className="btn-danger">← Leave Meeting</button>
        </div>
      </header>

      <div className="meeting-content">
        <LiveKitRoom
          token={livekitToken}
          serverUrl={livekitUrl}
          connect={!disconnected}
          audio={!disconnected}
          video={false}
          onDisconnected={handleDisconnect}
        >
          <RoomContent
            canSeeParticipants={canSeeParticipants}
            inviteToken={inviteToken}
            guestId={guestId}
          />
        </LiveKitRoom>
      </div>

      <PlayStorePrompt
        isOpen={showFeedback}
        onDone={handleFeedbackDone}
      />
    </div>
  );
}

export default GuestMeetingRoom;
